import {local} from 'lib/StorageUtil';
import {isSafeRedirectPath} from 'lib/isSafeRedirectPath';

export const LOG_IN_PAGE_BASE_PATH = '/log-in';

const STORAGE_KEY = 'postLoginRedirect';

// Remembering an auth page would drop the user back into the flow they just finished.
const AUTH_PATHS = new Set([
  LOG_IN_PAGE_BASE_PATH,
  '/signup',
  '/social',
  '/forgot-password',
  '/reset-password'
]);

function isStorablePath(path: unknown): path is string {
  if (!isSafeRedirectPath(path)) {
    return false;
  }
  const [pathname] = path.split(/[?#]/);
  return !AUTH_PATHS.has(pathname);
}

/**
 * Remember where the user was headed so they can be returned there once they authenticate.
 * Off-origin and auth-page paths are dropped here rather than at each call site, so a caller can
 * hand over a raw location without having to pre-validate it.
 */
export function storePostLoginRedirect(path: string): void {
  if (isStorablePath(path)) {
    local.set(STORAGE_KEY, path);
  }
}

/**
 * Read the remembered destination and clear it in the same breath. Clearing even when the value is
 * rejected is what keeps a stale (or tampered) entry from hijacking a later, unrelated login.
 */
export function consumePostLoginRedirect(): string | null {
  const stored = local.get(STORAGE_KEY);
  if (!stored) {
    return null;
  }
  local.remove(STORAGE_KEY);
  return isStorablePath(stored) ? stored : null;
}

/**
 * Drop the remembered destination for a login that navigates nowhere (the modal leaves the user on
 * the page they were already on). The value means "where the NEXT login should land", so leaving it
 * behind would send a later, unrelated login to a page the user has since moved on from.
 */
export function clearPostLoginRedirect(): void {
  local.remove(STORAGE_KEY);
}
